import {
  type StreamingCallbacks,
  type SignalingConfig,
  type SignalingMessage,
  ConnectionErrorCode,
} from "../types";
import type {
  SystemMessage,
  LatencyMeasurementMessage,
  SystemStatsMessage,
  PipelineMessage,
  ClipboardMessage,
  CursorMessage,
  GpuStatsMessage,
} from "../types/messages";
import { SystemEventHandler } from "./SystemEvents";
import { InputHandler } from "./InputHandler";
import { SignalingService } from "./SignalingService";
import { DataChannelMessageHandler } from "./DataChannelMessageHandler";
import { MediaStreamId, MediaStreamLabel } from "../constants/webrtc";
import type {
  GeneralRTCStats,
  Reports,
  RTCConnectionStats,
} from "../types/eonStats";
import { EventManager } from "../input";

export class WebRTCConnection {
  private peerConnection: RTCPeerConnection | null = null;
  private callbacks: StreamingCallbacks;
  private signalingService?: SignalingService;
  private dataChannel?: RTCDataChannel;
  private inputHandler?: InputHandler;
  private lastShouldPoll = true;
  private lastMenuOpen = false;
  private videoElement?: HTMLVideoElement;
  private audioElement?: HTMLAudioElement;
  private rtcPeerConfig?: RTCConfiguration;
  private cursorCache = new Map();
  private mediaStreamId: MediaStreamId = MediaStreamId.Video;
  private readonly MAX_BUFFER_SIZE = 16 * 1024 * 1024; // 16MB
  private readonly eventManager: EventManager;
  private readonly systemEventHandler: SystemEventHandler;
  private readonly isTV: boolean = false;

  private isResetting = false;
  private isConnecting = false;
  public isDisconnected = false;
  private wasIceDisconnected = false;

  private resetAttempts = 0;

  private cursorBeforeFullscreenExit = "auto";

  private onFullscreenChange = () => {
    if (!this.videoElement) return;
    if (document.fullscreenElement) {
      this.videoElement.style.cursor = this.cursorBeforeFullscreenExit;
    } else if (this.videoElement.style.cursor === "none") {
      this.videoElement.style.cursor = "auto";
    }
  };

  private onVideoLoadedData = () => {
    this.inputHandler?.getCursorScaleFactor();
  };

  private registerUnloadHandler(): void {
    const { addListener } = this.eventManager;
    addListener(window, "beforeunload", this.syncCleanup.bind(this));
  }

  constructor({
    callbacks = {},
    videoElement,
    audioElement,
    rtcConfiguration,
    signalingConfig,
    mediaStreamId,
    isTV = false,
  }: {
    callbacks: StreamingCallbacks;
    videoElement?: HTMLVideoElement;
    audioElement?: HTMLAudioElement;
    rtcConfiguration?: RTCConfiguration;
    signalingConfig?: SignalingConfig;
    mediaStreamId?: MediaStreamId;
    isTV?: boolean;
  }) {
    this.callbacks = callbacks;
    this.videoElement = videoElement;
    this.audioElement = audioElement;
    this.rtcPeerConfig = rtcConfiguration;
    this.isTV = isTV;
    this.eventManager = new EventManager();

    if (this.videoElement) {
      this.inputHandler = new InputHandler(
        this.videoElement,
        (message) => {
          this.sendDataChannelMessage(message);
        },
        { isTV },
      );
      this.inputHandler.onfullscreenchange = this.onFullscreenChange;
    }

    if (this.videoElement) {
      const { addListener } = this.eventManager;
      addListener(this.videoElement, "loadeddata", this.onVideoLoadedData);
    }

    // Initialize signaling service if config provided
    if (signalingConfig && mediaStreamId) {
      this.mediaStreamId = mediaStreamId;

      this.initializeSignaling(signalingConfig, mediaStreamId);
    }
    this.registerUnloadHandler();

    this.systemEventHandler = new SystemEventHandler(
      this.videoElement,
      this.inputHandler?.getCursorScaleFactor,
      this.callbacks.onSystemEvent,
    );
  }

  private initializeSignaling(
    signalingConfig: SignalingConfig,
    mediaStreamId: MediaStreamId,
  ): void {
    const signalingCallbacks = {
      onMessage: this.handleSignalingMessage.bind(this),
      onClose: this.handleSignalingClose.bind(this),
      onLoadingPercentage:
        mediaStreamId === MediaStreamId.Video
          ? this.callbacks.onLoadingPercentage
          : undefined,
      onOpen: () => {
        console.log(
          `[${MediaStreamLabel[this.mediaStreamId]}] - Signaling connected`,
        );
      },
      onError: (error: Error) => {
        this.callbacks.onError?.({
          media: MediaStreamLabel[this.mediaStreamId],
          code: ConnectionErrorCode.SIGNALING_CONNECTION_FAILED,
          message: `Signaling error: ${error.message}`,
        });
      },
      onChangeSignalingStatus: this.callbacks.onChangeSignalingStatus,
    };

    this.signalingService = new SignalingService(
      signalingConfig,
      mediaStreamId,
      signalingCallbacks,
    );
  }

  private async handleSignalingMessage(
    message: SignalingMessage,
  ): Promise<void> {
    try {
      if (message.sdp) {
        await this.createAnswer(new RTCSessionDescription(message.sdp));
      }
      if (message.ice?.candidate) {
        await this.addIceCandidate(new RTCIceCandidate(message.ice));
      }
      this.callbacks.onSignalingMessage?.(message);
    } catch (error) {
      console.error("Signaling message handling error", error);
    }
  }

  private handleSignalingClose(event?: CloseEvent): void {
    if (this.isDisconnected || this.isResetting) return;

    // Check peer connection state
    const pcState = this.peerConnection?.connectionState;
    const iceState = this.peerConnection?.iceConnectionState;

    console.log(
      `[${
        MediaStreamLabel[this.mediaStreamId]
      }] Signaling closed - PC state: ${pcState}, ICE state: ${iceState}`,
    );
    //
    // We require close code 1000 (normal closure) specifically, because
    // other "connected"-state closes mean something else:
    //   - 1011 (server error, "keepalive ping timeout"): the server detected
    //     WE stopped responding and closed us — that's a client-side network
    //     drop, not a takeover. Handled by the existing reclaim flow.
    //   - 1006 (abnormal): the TCP/TLS connection broke without a close
    //     frame — always a network issue, never a takeover.

    if (pcState === "connected" && event?.code === 1000) {
      this.callbacks.onStreamEnded?.();
      return;
    }

    const isNewAndSignalingFailed = pcState === "new" && !this.isConnecting;

    if (isNewAndSignalingFailed) {
      // Allow more retry attempts before giving up (total wait: 2+4+8+10+10 = 34s)
      if (this.resetAttempts < 7) {
        console.log(
          `[${
            MediaStreamLabel[this.mediaStreamId]
          }] Signaling closed at "new" state, retrying (attempt ${
            this.resetAttempts + 1
          }/7)`,
        );

        this.reset();
        return;
      }

      const errorMessage = `[${
        MediaStreamLabel[this.mediaStreamId]
      }] Connection repeatedly fails at "new" state. Another tab may be connected.`;

      console.warn(errorMessage);
      this.callbacks.onError?.({
        media: MediaStreamLabel[this.mediaStreamId],
        code: ConnectionErrorCode.REPEATED_CONNECTION_FAILURE,
        message: errorMessage,
      });
    }
  }

  setSignalingService(signalingService: SignalingService): void {
    this.signalingService = signalingService;
  }

  setRtcPeerConfiguration(rtcPeerConfig: RTCConfiguration): void {
    this.rtcPeerConfig = rtcPeerConfig;
  }

  updateMapping(): void {
    this.inputHandler?.updateMapping();
  }

  async connect() {
    if (this.isConnecting) {
      console.log(
        `[${
          MediaStreamLabel[this.mediaStreamId]
        }] - WebRTC connection already in progress`,
      );
      return;
    }
    this.isConnecting = true;
    console.log(
      `[${MediaStreamLabel[this.mediaStreamId]}] - WebRTC connecting...`,
    );

    this.peerConnection = new RTCPeerConnection(this.rtcPeerConfig);
    this.setupEventHandlers();
    try {
      await this.signalingService?.connect();
    } catch (error) {
      console.error(
        `[${
          MediaStreamLabel[this.mediaStreamId]
        }] - Failed to connect to signaling server`,
        error,
      );

      this.callbacks.onError?.({
        media: MediaStreamLabel[this.mediaStreamId],
        code: ConnectionErrorCode.SIGNALING_CONNECTION_FAILED,
        message: `Failed to connect to signaling server: ${
          error instanceof Error ? error.message : String(error)
        }`,
      });
    } finally {
      this.isConnecting = false;
    }
  }

  private onPeerDataChannel(event: RTCDataChannelEvent) {
    this.dataChannel = event.channel;
    this.dataChannel.onmessage = this.onDataChannelMessage.bind(this);
    this.dataChannel.onopen = () => {
      console.log(
        `[${MediaStreamLabel[this.mediaStreamId]}] [data channel] - opened`,
      );
      this.resetAttempts = 0;
      this.inputHandler?.attach();
      if (this.videoElement) {
        this.inputHandler?.enableInputLocking();
      }
      this.callbacks.onDataChannelOpen?.();
    };

    this.dataChannel.onclose = () => {
      console.log(
        `[${MediaStreamLabel[this.mediaStreamId]}] [data channel] - closed`,
      );
      this.inputHandler?.detach();
      this.callbacks.onDataChannelClose?.();

      // Reset if connection is still good
      if (!this.isDisconnected && !this.isResetting) {
        const pcState = this.peerConnection?.connectionState;

        if (pcState === "connected") {
          console.log(
            `[${
              MediaStreamLabel[this.mediaStreamId]
            }] Data channel closed while connected`,
          );
          this.reset();
        }
      }
    };
    this.dataChannel.onerror = (error) => {
      console.error(
        `[${MediaStreamLabel[this.mediaStreamId]}] [data channel] - error: `,
        error,
      );
      this.callbacks.onDataChannelError?.(error);
    };
  }

  private checkAndNotifyReconnected(): void {
    if (!this.peerConnection) return;

    const iceState = this.peerConnection.iceConnectionState;
    const pcState = this.peerConnection.connectionState;
    const isIceHealthy = iceState === "connected" || iceState === "completed";
    const isPcHealthy = pcState === "connected";

    // Only fire onNetworkReconnected for video connections
    if (
      isIceHealthy &&
      isPcHealthy &&
      this.mediaStreamId === MediaStreamId.Video
    ) {
      this.callbacks?.onNetworkReconnected?.();
    }
  }

  private setupEventHandlers(): void {
    if (!this.peerConnection) return;

    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection!.connectionState;
      console.log(
        `[${MediaStreamLabel[this.mediaStreamId]}]`,
        "[peerConnection] onconnectionstatechange",
        this.peerConnection?.connectionState,
      );

      if (state === "failed" && !this.isDisconnected) {
        this.callbacks?.onNetworkDisconnect?.();
      }

      if (state === "connected") {
        this.resetAttempts = 0;
        // Check if both ICE and peer connection are healthy
        this.checkAndNotifyReconnected();
      }

      this.callbacks.onConnectionStateChange?.({
        connectionState: state,
        isConnected: state === "connected",
        hasRemoteStream: this.hasRemoteStream(),
      });
    };

    this.peerConnection.onicecandidate = (event) => {
      console.log(
        `[${MediaStreamLabel[this.mediaStreamId]}]`,
        "[peerConnection] onicecandidate",
        event.candidate,
      );
      if (event.candidate && this.signalingService) {
        this.signalingService.sendIceCandidate(event.candidate);
      }
    };

    this.peerConnection.ontrack = (event) => {
      const [remoteStream] = event.streams;
      if (remoteStream) {
        this.callbacks.onStreamReceived?.(remoteStream);
      }
    };

    this.peerConnection.oniceconnectionstatechange = () => {
      const iceState = this.peerConnection!.iceConnectionState;
      console.log(
        `[${MediaStreamLabel[this.mediaStreamId]}] ICE connection state:`,
        iceState,
      );

      if (iceState === "disconnected" && !this.isDisconnected) {
        console.log(
          `[${MediaStreamLabel[this.mediaStreamId]}] ICE disconnected`,
        );
        this.wasIceDisconnected = true;
        this.callbacks?.onIceDisconnected?.();
      } else if (iceState === "connected" || iceState === "completed") {
        if (this.wasIceDisconnected) {
          this.wasIceDisconnected = false;
          console.log(
            `[${MediaStreamLabel[this.mediaStreamId]}] ICE reconnected`,
          );
          this.callbacks?.onIceReconnected?.();
        }
        // Check if both ICE and peer connection are healthy
        this.checkAndNotifyReconnected();
      }
    };

    this.peerConnection.onicecandidateerror = (event) => {
      console.log("ICE connection error:", event.errorText);
    };

    this.peerConnection.ondatachannel = this.onPeerDataChannel.bind(this);
  }

  async createOffer(
    options?: RTCOfferOptions,
  ): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error("Peer connection not initialized");
    }

    const offer = await this.peerConnection.createOffer(options);
    await this.peerConnection.setLocalDescription(offer);
    return offer;
  }

  async createAnswer(
    offer: RTCSessionDescriptionInit,
    options?: RTCAnswerOptions,
  ): Promise<RTCSessionDescriptionInit> {
    if (!this.peerConnection) {
      throw new Error(
        `[${
          MediaStreamLabel[this.mediaStreamId]
        }] - Peer connection not initialized`,
      );
    }

    await this.peerConnection?.setRemoteDescription(offer);
    const answer = await this.peerConnection?.createAnswer(options);
    // Set sps-pps-idr-in-keyframe=1
    if (
      answer?.sdp &&
      !/[^-]sps-pps-idr-in-keyframe=1[^\d]/gm.test(answer.sdp) &&
      /[^-]packetization-mode=/gm.test(answer.sdp)
    ) {
      console.log("Overriding WebRTC SDP to include sps-pps-idr-in-keyframe=1");
      if (/[^-]sps-pps-idr-in-keyframe=\d+/gm.test(answer.sdp)) {
        answer.sdp = answer.sdp.replace(
          /sps-pps-idr-in-keyframe=\d+/gm,
          "sps-pps-idr-in-keyframe=1",
        );
      } else {
        answer.sdp = answer.sdp.replace(
          "packetization-mode=",
          "sps-pps-idr-in-keyframe=1;packetization-mode=",
        );
      }
    }
    if (answer.sdp?.indexOf("multiopus") === -1) {
      // Override SDP to enable stereo on WebRTC Opus with Chromium, must be munged before the Local Description
      if (
        !/[^-]stereo=1[^\d]/gm.test(answer.sdp) &&
        /[^-]useinbandfec=/gm.test(answer.sdp)
      ) {
        console.log("Overriding WebRTC SDP to allow stereo audio");
        if (/[^-]stereo=\d+/gm.test(answer.sdp)) {
          answer.sdp = answer.sdp.replace(/stereo=\d+/gm, "stereo=1");
        } else {
          answer.sdp = answer.sdp.replace(
            "useinbandfec=",
            "stereo=1;useinbandfec=",
          );
        }
      }
      // OPUS_FRAME: Override SDP to reduce packet size to 10 ms
      if (
        !/[^-]minptime=10[^\d]/gm.test(answer.sdp) &&
        /[^-]useinbandfec=/gm.test(answer.sdp)
      ) {
        console.log("Overriding WebRTC SDP to allow low-latency audio packet");
        if (/[^-]minptime=\d+/gm.test(answer.sdp)) {
          answer.sdp = answer.sdp.replace(/minptime=\d+/gm, "minptime=10");
        } else {
          answer.sdp = answer.sdp.replace(
            "useinbandfec=",
            "minptime=10;useinbandfec=",
          );
        }
      }
    }
    await this.peerConnection.setLocalDescription(answer);
    this.peerConnection.localDescription &&
      this.signalingService?.sendSDP(this.peerConnection.localDescription);

    return answer;
  }

  async setRemoteDescription(
    description: RTCSessionDescriptionInit,
  ): Promise<void> {
    await this.peerConnection?.setRemoteDescription(description);
  }

  async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    await this.peerConnection?.addIceCandidate(candidate);
  }

  private hasRemoteStream(): boolean {
    if (!this.peerConnection) return false;
    return this.peerConnection
      .getReceivers()
      .some((receiver) => receiver.track !== null);
  }

  private onCursorChange({
    handle,
    curdata,
    hotspot,
    override,
  }: {
    handle: number;
    curdata?: string;
    hotspot?: { x: number; y: number };
    override?: string;
  }) {
    if (!this.videoElement) return;

    if (handle === 0) {
      this.cursorBeforeFullscreenExit = "auto";
      this.videoElement.style.cursor = "auto";
      return;
    }

    if (override) {
      this.cursorBeforeFullscreenExit = override;
      this.videoElement.style.cursor = override;
      return;
    }

    let cursorUrl = this.cursorCache.get(handle);
    if (!cursorUrl) {
      cursorUrl = `url('data:image/png;base64,${curdata}')`;
      this.cursorCache.set(handle, cursorUrl);
    }

    cursorUrl = hotspot
      ? `${cursorUrl} ${hotspot.x} ${hotspot.y}, auto`
      : `${cursorUrl}, auto`;

    this.cursorBeforeFullscreenExit = cursorUrl;
    this.videoElement.style.cursor = cursorUrl;
  }

  private onClipboard(content: string) {
    navigator.clipboard.writeText(content).catch((err) => {
      console.log("Could not copy text to clipboard: " + err);
    });
  }

  private messageHandler = new DataChannelMessageHandler({
    onSystem: (message: SystemMessage) => {
      if (message.action !== null && message.data?.action) {
        console.log("Received system message, action:", message.data.action);

        try {
          this.systemEventHandler.handleSystemMessage(message.data.action);
        } catch (error) {
          console.error("Error handling system message:", error);
        }
      }
    },

    onPing: () => {
      if (this.dataChannel?.readyState === "open") {
        try {
          this.dataChannel.send(`pong,${Date.now() / 1000}`);
        } catch (error) {
          console.error("Error sending pong message:", error);
        }
      }
    },

    onLatencyMeasurement: (message: LatencyMeasurementMessage) => {
      this.callbacks.onLatencyMeasurement?.(message.data.latency_ms * 2.0);
    },

    onSystemStats: (message: SystemStatsMessage) => {
      this.callbacks.onSystemStats?.(message.data);
    },

    onPipeline: (message: PipelineMessage) => {
      console.log("Pipeline status: ", message.data.status);
    },
    onClipboard: (message: ClipboardMessage) => {
      this.onClipboard(message.data.content);
    },
    onCursor: (message: CursorMessage) => {
      this.onCursorChange(message.data);
    },
    onGpuStats: (message: GpuStatsMessage) => {
      this.callbacks.onGpuStats?.({
        ...message.data,
        load: Math.round(message.data.load * 100),
      });
    },
    onRumble: (message) => this.inputHandler?.handleRumble(message),
  });

  private onDataChannelMessage(event: MessageEvent<any>) {
    this.messageHandler.handleMessage(event);
  }

  runRumble(index = 0): Promise<string | undefined> {
    if (!this.inputHandler) {
      console.warn("[rumble] InputHandler not available");
      return Promise.resolve(undefined);
    }
    return this.inputHandler.runRumble(index);
  }

  runRumbleAll(): string | undefined {
    if (!this.inputHandler) return undefined;
    return this.inputHandler.runRumbleAll();
  }

  getConnectionState(): RTCPeerConnectionState | null {
    return this.peerConnection?.connectionState || null;
  }

  getLocalDescription(): RTCSessionDescription | null {
    return this.peerConnection?.localDescription || null;
  }

  adjustVideoBitrate(bitrateKbps: number): void {
    if (!this.dataChannel) return;
    this.dataChannel.send(`vb,${bitrateKbps}`);
  }

  adjustFps(fps: number): void {
    if (!this.dataChannel) return;
    this.dataChannel.send(`_arg_fps,${fps}`);
  }

  sendDataChannelMessage(data: any): void {
    if (this.dataChannel?.readyState === "open") {
      if (this.dataChannel.bufferedAmount > this.MAX_BUFFER_SIZE) {
        console.warn(
          `Buffer full (${this.dataChannel.bufferedAmount} bytes), skipping message`,
        );
        return;
      }

      try {
        const message = typeof data === "string" ? data : JSON.stringify(data);
        this.dataChannel.send(message);
      } catch (error) {
        console.error("Error sending data channel message:", error);
      }
    } else {
      console.warn(
        `[${MediaStreamLabel[this.mediaStreamId]}]`,
        "Data channel is not open. Current state:",
        this.dataChannel?.readyState,
      );
    }
  }

  async isMediaFlowing(): Promise<boolean> {
    if (!this.peerConnection) return false;

    try {
      const getBytes = async (): Promise<number> => {
        const stats = await this.peerConnection!.getStats();
        let bytes = 0;
        stats.forEach((report) => {
          if (report.type === "inbound-rtp") {
            bytes += report.bytesReceived || 0;
          }
        });
        return bytes;
      };

      const before = await getBytes();
      await new Promise((resolve) => setTimeout(resolve, 500));
      const after = await getBytes();

      return after > before;
    } catch {
      return false;
    }
  }

  isDataChannelOpen(): boolean {
    return this.dataChannel?.readyState === "open";
  }

  getVirtualGamepadSender(): ((message: string) => void) | null {
    if (this.isDataChannelOpen()) {
      return (message: string) => this.sendDataChannelMessage(message);
    }
    return null;
  }

  startGamepadPolling(): void {
    this.inputHandler?.startGamepadPolling();
  }

  stopGamepadPolling(): void {
    this.inputHandler?.stopGamepadPolling();
  }

  setStreamStatus(streamStatus: any): void {
    this.inputHandler?.setStreamStatus(streamStatus);
  }

  setShouldPoll(shouldPoll: boolean): void {
    this.lastShouldPoll = shouldPoll;
    this.inputHandler?.setShouldPoll(shouldPoll);
  }

  setMenuOpen(isMenuOpen: boolean): void {
    this.lastMenuOpen = isMenuOpen;
    this.inputHandler?.setMenuOpen(isMenuOpen);
  }

  sendKey(key: string): void {
    this.inputHandler?.sendKey(key);
  }

  toggleMouseSimulation(): void {
    this.inputHandler?.toggleMouseSimulation();
  }

  getMouseSimulator() {
    return this.inputHandler?.getMouseSimulator();
  }

  setDebugMode(enabled: boolean): void {
    this.inputHandler?.setDebugMode(enabled);
  }

  close(): void {
    if (this.dataChannel && this.dataChannel.readyState === "open") {
      this.dataChannel.close();
    }
    this.peerConnection?.close();
    this.peerConnection = null;
    if (this.videoElement) {
      this.eventManager.removeAllListeners();
    }
  }

  async getConnectionStats(): Promise<RTCConnectionStats> {
    if (!this.peerConnection) {
      throw new Error("Peer connection not initialized");
    }

    const stats = await this.peerConnection.getStats();

    const reports: Reports = {
      transports: {},
      candidatePairs: {},
      selectedCandidatePairId: "",
      remoteCandidates: {},
      codecs: {},
      videoRTP: null,
      videoTrack: null,
      audioRTP: null,
      audioTrack: null,
      dataChannel: null,
    };

    let videoMediaSource: Record<string, any> = {};

    // First pass: collect all reports
    stats.forEach((report) => {
      switch (report.type) {
        case "transport":
          reports.transports[report.id] = report;
          break;
        case "candidate-pair":
          reports.candidatePairs[report.id] = report;
          if (report.selected === true) {
            reports.selectedCandidatePairId = report.id;
          }
          break;
        case "inbound-rtp":
          if (report.kind === "video") {
            reports.videoRTP = report;
          } else if (report.kind === "audio") {
            reports.audioRTP = report;
          }
          break;
        case "remote-candidate":
          reports.remoteCandidates[report.id] = report;
          break;
        case "codec":
          reports.codecs[report.id] = report;
          break;
        case "data-channel":
          reports.dataChannel = report;
          break;
        case "media-source":
        case "track":
          if (report.kind === "video") {
            videoMediaSource = report;
          }
          break;
      }
    });

    // Initialize stats objects
    const videoStats = {
      packetsReceived: 0,
      packetsLost: 0,
      bytesReceived: 0,
      codecName: "NA",
      decoder: "NA",
      frameWidth: 0,
      frameHeight: 0,
      framesPerSecond: 0,
      framesDecoded: 0,
      framesDropped: 0,
      totalDecodeTime: 0,
      jitterBufferDelay: 0,
      jitterBufferEmittedCount: 0,
      jitter: 0,
      freezeCount: 0,
      totalFreezesDuration: 0,
      nackCount: 0,
      pliCount: 0,
    };

    const audioStats = {
      packetsReceived: 0,
      packetsLost: 0,
      bytesReceived: 0,
      codecName: "NA",
      jitterBufferDelay: 0,
      jitterBufferEmittedCount: 0,
      jitter: 0,
    };

    const generalStats: GeneralRTCStats = {
      currentRoundTripTime: 0,
      connectionType: "NA",
      bytesReceived: 0,
      bytesSent: 0,
      availableReceiveBandwidth: 0,
      packetsReceived: 0,
      packetsLost: 0,
    };

    const dataStats = {
      bytesReceived: 0,
      bytesSent: 0,
      messagesReceived: 0,
      messagesSent: 0,
    };

    // Extract video stats
    if (reports.videoRTP) {
      videoStats.packetsReceived = reports.videoRTP.packetsReceived || 0;
      videoStats.packetsLost = reports.videoRTP.packetsLost || 0;
      videoStats.bytesReceived = reports.videoRTP.bytesReceived || 0;
      videoStats.decoder = reports.videoRTP.decoderImplementation || "unknown";
      videoStats.frameWidth =
        reports.videoRTP.frameWidth || videoMediaSource?.frameWidth || 0;
      videoStats.frameHeight =
        reports.videoRTP.frameHeight || videoMediaSource?.frameHeight || 0;
      videoStats.framesPerSecond =
        reports.videoRTP.framesPerSecond ||
        videoMediaSource?.framesPerSecond ||
        0;
      videoStats.framesDecoded =
        reports.videoRTP.framesDecoded || videoMediaSource?.framesDecoded || 0;
      videoStats.framesDropped = reports.videoRTP.framesDropped || 0;
      videoStats.totalDecodeTime = reports.videoRTP.totalDecodeTime || 0;
      videoStats.jitterBufferDelay = reports.videoRTP.jitterBufferDelay || 0;
      videoStats.jitterBufferEmittedCount =
        reports.videoRTP.jitterBufferEmittedCount || 0;
      videoStats.jitter = (reports.videoRTP.jitter || 0) * 1000;
      videoStats.freezeCount = reports.videoRTP.freezeCount || 0;
      videoStats.totalFreezesDuration =
        reports.videoRTP.totalFreezesDuration || 0;
      videoStats.nackCount = reports.videoRTP.nackCount || 0;
      videoStats.pliCount = reports.videoRTP.pliCount || 0;

      // Extract video codec
      if (
        reports.videoRTP.codecId &&
        reports.codecs[reports.videoRTP.codecId]
      ) {
        const codec = reports.codecs[reports.videoRTP.codecId];
        videoStats.codecName =
          codec.mimeType?.split("/")[1]?.toUpperCase() || "NA";
      }
    }

    // Extract audio stats
    if (reports.audioRTP) {
      audioStats.packetsReceived = reports.audioRTP.packetsReceived || 0;
      audioStats.packetsLost = reports.audioRTP.packetsLost || 0;
      audioStats.bytesReceived = reports.audioRTP.bytesReceived || 0;
      audioStats.jitterBufferDelay = reports.audioRTP.jitterBufferDelay || 0;
      audioStats.jitterBufferEmittedCount =
        reports.audioRTP.jitterBufferEmittedCount || 0;
      audioStats.jitter = (reports.audioRTP.jitter || 0) * 1000;

      // Extract audio codec
      if (
        reports.audioRTP.codecId &&
        reports.codecs[reports.audioRTP.codecId]
      ) {
        const codec = reports.codecs[reports.audioRTP.codecId];
        audioStats.codecName =
          codec.mimeType?.split("/")[1]?.toUpperCase() || "NA";
      }
    }

    // Extract data channel stats
    if (reports.dataChannel) {
      dataStats.bytesReceived = reports.dataChannel.bytesReceived || 0;
      dataStats.bytesSent = reports.dataChannel.bytesSent || 0;
      dataStats.messagesReceived = reports.dataChannel.messagesReceived || 0;
      dataStats.messagesSent = reports.dataChannel.messagesSent || 0;
    }

    generalStats.packetsReceived =
      videoStats.packetsReceived + audioStats.packetsReceived;
    generalStats.packetsLost = videoStats.packetsLost + audioStats.packetsLost;

    // Extract transport stats (bytes sent/received)
    if (Object.keys(reports.transports).length > 0) {
      const transport = reports.transports[Object.keys(reports.transports)[0]];
      generalStats.bytesReceived = transport.bytesReceived || 0;
      generalStats.bytesSent = transport.bytesSent || 0;
      // Get selected candidate pair from transport
      if (transport.selectedCandidatePairId) {
        reports.selectedCandidatePairId = transport.selectedCandidatePairId;
      }
    } else if (
      reports.selectedCandidatePairId &&
      reports.candidatePairs[reports.selectedCandidatePairId]
    ) {
      // Fallback to candidate pair stats
      const pair = reports.candidatePairs[reports.selectedCandidatePairId];
      console.log("candidate pair", pair);
      generalStats.bytesReceived = pair.bytesReceived || 0;
      generalStats.bytesSent = pair.bytesSent || 0;
    }

    // Extract connection stats from selected candidate pair
    if (reports.selectedCandidatePairId) {
      const candidatePair =
        reports.candidatePairs[reports.selectedCandidatePairId];

      if (candidatePair.currentRoundTripTime !== undefined) {
        generalStats.currentRoundTripTime = candidatePair.currentRoundTripTime;
      }

      if (candidatePair.availableIncomingBitrate !== undefined) {
        generalStats.availableReceiveBandwidth =
          candidatePair.availableIncomingBitrate;
      }

      // Get connection type from remote candidate
      if (
        candidatePair.remoteCandidateId &&
        reports.remoteCandidates[candidatePair.remoteCandidateId]
      ) {
        const remoteCandidate =
          reports.remoteCandidates[candidatePair.remoteCandidateId];
        if (remoteCandidate.candidateType) {
          generalStats.connectionType = remoteCandidate.candidateType;
        }
      }
    }

    return {
      general: generalStats,
      video: videoStats,
      audio: audioStats,
      data: dataStats,
      allReports: Array.from(stats.values()),
      reports,
    };
  }

  disconnect(code?: number): void {
    this.isDisconnected = true;
    if (this.signalingService) {
      this.signalingService.disconnect(code);
    }

    if (this.peerConnection) {
      this.callbacks.onConnectionStateChange?.({
        connectionState: this.peerConnection.connectionState,
        isConnected: false,
        hasRemoteStream: this.hasRemoteStream(),
      });
    }

    this.close();
  }

  async reset(): Promise<void> {
    if (this.isResetting) {
      console.log(
        `[${
          MediaStreamLabel[this.mediaStreamId]
        }] - WebRTC reset already in progress`,
      );
      return;
    }

    this.isResetting = true;
    this.resetAttempts++;
    console.log(
      `[${
        MediaStreamLabel[this.mediaStreamId]
      }] - Resetting WebRTC connection... (attempt ${this.resetAttempts})`,
    );

    // Store references before cleanup
    const rtcPeerConfig = this.rtcPeerConfig;
    const signalingConfig = this.signalingService
      ? {
          url: this.signalingService["config"].url,
          protocols: this.signalingService["config"].protocols,
        }
      : undefined;

    this.cleanup();

    const delay = Math.min(2000 * Math.pow(2, this.resetAttempts - 1), 8000);

    console.log(
      `[${
        MediaStreamLabel[this.mediaStreamId]
      }] - Waiting ${delay}ms before reconnecting...`,
      this.isDisconnected,
    );

    await new Promise((resolve) => setTimeout(resolve, delay));

    // Restore references that were cleared
    this.rtcPeerConfig = rtcPeerConfig;

    // Reinitialize input handler if video element exists
    if (this.videoElement) {
      this.inputHandler = new InputHandler(
        this.videoElement,
        (message) => {
          this.sendDataChannelMessage(message);
        },
        { isTV: this.isTV },
      );
      this.inputHandler.onfullscreenchange = this.onFullscreenChange;
      this.inputHandler.setShouldPoll(this.lastShouldPoll);
      this.inputHandler.setMenuOpen(this.lastMenuOpen);
      this.eventManager.addListener(
        this.videoElement,
        "loadeddata",
        this.onVideoLoadedData,
      );
    }

    // Reinitialize signaling if it existed
    if (signalingConfig) {
      this.initializeSignaling(signalingConfig, this.mediaStreamId);
    }

    // Reconnect only if not deliberately disconnected
    if (!this.isDisconnected) {
      await this.connect();
    }

    this.isResetting = false;

    console.log(
      `[${
        MediaStreamLabel[this.mediaStreamId]
      }] - WebRTC connection reset complete after ${delay}ms`,
    );
  }

  private closeDataChannel(): void {
    if (!this.dataChannel) return;

    console.log(
      `[${
        MediaStreamLabel[this.mediaStreamId]
      }] - Closing data channel, initial state: ${
        this.dataChannel.readyState
      }, buffered: ${this.dataChannel.bufferedAmount}`,
    );

    if (
      this.dataChannel.readyState === "open" ||
      this.dataChannel.readyState === "connecting"
    ) {
      this.dataChannel.close();
    }

    this.dataChannel = undefined;
  }

  cleanup() {
    console.log(
      `=== [${MediaStreamLabel[this.mediaStreamId]}] - Starting Cleanup ===`,
    );

    this.isConnecting = false;
    this.wasIceDisconnected = false;
    this.cursorBeforeFullscreenExit = "auto";

    // 1. Detach input handler to stop new messages
    if (this.inputHandler) {
      console.log(
        `[${MediaStreamLabel[this.mediaStreamId]}] - Detaching input handler`,
      );
      this.inputHandler.detach();
      this.inputHandler = undefined;
    }

    // 2. Close data channel (while peer connection is still open)
    if (this.dataChannel) {
      this.closeDataChannel();
    }

    // 3. Close peer connection — .close() handles stopping transceivers,
    //    tracks, and making event handlers inert internally.
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    // 4. Clear media elements
    if (this.videoElement) {
      this.videoElement.srcObject = null;
      this.videoElement.pause();
      this.videoElement.load();
    }

    if (this.audioElement) {
      this.audioElement.srcObject = null;
      this.audioElement.pause();
      this.audioElement.load();
    }

    // 5. Disconnect signaling LAST
    if (this.signalingService) {
      this.signalingService.disconnect();
      this.signalingService = undefined;
    }

    this.eventManager.removeAllListeners();

    console.log(
      `=== [${MediaStreamLabel[this.mediaStreamId]}] - Cleanup Complete ===`,
    );
  }

  // Synchronous cleanup for page unload
  private syncCleanup(): void {
    console.log(
      `=== [${MediaStreamLabel[this.mediaStreamId]}] - Synchronous Cleanup ===`,
    );

    // Mark as disconnected to prevent reset attempts
    this.isDisconnected = true;

    // 1. Detach input handler
    if (this.inputHandler) {
      this.inputHandler.detach();
      this.inputHandler = undefined;
    }

    // Close data channel immediately
    if (this.dataChannel) {
      if (
        this.dataChannel.readyState === "open" ||
        this.dataChannel.readyState === "connecting"
      ) {
        this.dataChannel.close();
      }
      this.dataChannel = undefined;
    }

    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
      this.videoElement.pause();
    }

    if (this.audioElement) {
      this.audioElement.srcObject = null;
      this.audioElement.pause();
    }

    if (this.signalingService) {
      this.signalingService.disconnect();
    }

    this.eventManager.removeAllListeners();

    console.log(
      `=== [${
        MediaStreamLabel[this.mediaStreamId]
      }] - Synchronous Cleanup Complete ===`,
    );
  }
}
